/**
 * Invoice PDF General Section Utilities
 *
 * Comprehensive utilities for managing general information section
 * in invoice forms, inspired by easy-invoice-pdf patterns.
 *
 * Features:
 * - Template selection and configuration
 * - Logo upload validation and processing
 * - Language selection with form field updates
 * - Currency selection and formatting
 * - Date format selection and preview
 * - Invoice number management with month/year extraction
 * - Date of issue and service validation
 * - Payment due date calculation
 * - Template-specific field visibility
 * - Form field helper messages
 */

// ============================================================================
// TEMPLATE CONFIGURATION
// ============================================================================

/**
 * Supported invoice templates
 * @type {Array<string>}
 */
export const SUPPORTED_TEMPLATES = ['default', 'stripe'];

/**
 * Template labels for display
 * @type {Object}
 */
export const TEMPLATE_TO_LABEL = {
  default: 'Default Template',
  stripe: 'Stripe Template'
};

/**
 * Default date format for Stripe template
 * @type {string}
 */
export const STRIPE_DEFAULT_DATE_FORMAT = 'MMMM D, YYYY';

/**
 * Get template label
 * @param {string} template - Template name
 * @returns {string} Template label
 */
export function getTemplateLabel(template) {
  return TEMPLATE_TO_LABEL[template] || template;
}

/**
 * Check if template is Stripe
 * @param {string} template - Template name
 * @returns {boolean} True if Stripe template
 */
export function isStripeTemplate(template) {
  return template === 'stripe';
}

/**
 * Check if template is default
 * @param {string} template - Template name
 * @returns {boolean} True if default template
 */
export function isDefaultTemplate(template) {
  return template === 'default';
}

/**
 * Get template-specific configuration
 * @param {string} template - Template name
 * @returns {Object} Template configuration
 */
export function getTemplateConfig(template) {
  if (isStripeTemplate(template)) {
    return {
      supportsLogo: true,
      supportsPaymentLink: true,
      defaultDateFormat: STRIPE_DEFAULT_DATE_FORMAT,
      vatFieldAlwaysVisible: true
    };
  }

  return {
    supportsLogo: false,
    supportsPaymentLink: false,
    defaultDateFormat: 'YYYY-MM-DD',
    vatFieldAlwaysVisible: false
  };
}

// ============================================================================
// LOGO UPLOAD UTILITIES
// ============================================================================

/**
 * Maximum logo file size in bytes (3MB)
 * @type {number}
 */
export const MAX_LOGO_SIZE = 3 * 1024 * 1024;

/**
 * Valid logo file types
 * @type {Array<string>}
 */
export const VALID_LOGO_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

/**
 * Validate logo file size
 * @param {File} file - File to validate
 * @returns {Promise<boolean>} True if valid size
 */
export function validateLogoSize(file) {
  return Promise.resolve(file.size <= MAX_LOGO_SIZE);
}

/**
 * Validate logo file type
 * @param {File} file - File to validate
 * @returns {boolean} True if valid type
 */
export function validateLogoType(file) {
  return VALID_LOGO_TYPES.includes(file.type);
}

/**
 * Convert file to base64
 * @param {File} file - File to convert
 * @returns {Promise<string>} Base64 string
 */
export function convertFileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Validate and process logo upload
 * @param {File} file - File to process
 * @returns {Promise<Object>} Result with success status and data/error
 */
export async function processLogoUpload(file) {
  if (!file) {
    return { success: false, error: 'No file provided' };
  }

  if (!validateLogoType(file)) {
    return { success: false, error: 'Please select a valid image file (JPEG, PNG or WebP)' };
  }

  const isValidSize = await validateLogoSize(file);
  if (!isValidSize) {
    return { success: false, error: 'Image size must be less than 3MB' };
  }

  try {
    const base64 = await convertFileToBase64(file);
    return { success: true, data: base64 };
  } catch (error) {
    return { success: false, error: 'Error uploading image. Please try again.' };
  }
}

/**
 * Get logo upload helper text
 * @returns {string} Helper text
 */
export function getLogoUploadHelperText() {
  return 'JPEG, PNG or WebP (max 3MB)';
}

/**
 * Get logo upload success message
 * @returns {string} Success message
 */
export function getLogoUploadSuccessMessage() {
  return 'Logo uploaded successfully. Click the X to remove it.';
}

// ============================================================================
// LANGUAGE CONFIGURATION
// ============================================================================

/**
 * Supported languages
 * @type {Array<string>}
 */
export const SUPPORTED_LANGUAGES = ['en', 'de', 'es', 'pt', 'it', 'nl', 'pl', 'ru', 'uk', 'fr'];

/**
 * Language labels for display
 * @type {Object}
 */
export const LANGUAGE_TO_LABEL = {
  en: 'English',
  de: 'Deutsch (German)',
  es: 'Espanol (Spanish)',
  pt: 'Portugues (Portuguese)',
  it: 'Italiano (Italian)',
  nl: 'Nederlands (Dutch)',
  pl: 'Polski (Polish)',
  ru: 'Russkiy (Russian)',
  uk: 'Ukrayinska (Ukrainian)',
  fr: 'Francais (French)'
};

/**
 * Get language label
 * @param {string} language - Language code
 * @returns {string} Language label
 */
export function getLanguageLabel(language) {
  return LANGUAGE_TO_LABEL[language] || language;
}

/**
 * Check if language is supported
 * @param {string} language - Language code
 * @returns {boolean} True if supported
 */
export function isLanguageSupported(language) {
  return SUPPORTED_LANGUAGES.includes(language);
}

/**
 * Invoice number labels by language
 * @type {Object}
 */
export const INVOICE_NUMBER_LABELS = {
  en: 'Invoice Number',
  de: 'Rechnungsnummer',
  es: 'Numero de factura',
  pt: 'Numero da fatura',
  it: 'Numero fattura',
  nl: 'Factuurnummer',
  pl: 'Numer faktury',
  ru: 'Nomer scheta',
  uk: 'Nomer rakhunku',
  fr: 'Numero de facture'
};

/**
 * VAT/Tax number labels by language
 * @type {Object}
 */
export const VAT_NUMBER_LABELS = {
  en: 'VAT No',
  de: 'USt-Nr',
  es: 'NIF',
  pt: 'NIF',
  it: 'P.IVA',
  nl: 'BTW-nr',
  pl: 'NIP',
  ru: 'INN',
  uk: 'IPN',
  fr: 'N TVA'
};

/**
 * Tax column labels by language
 * @type {Object}
 */
export const TAX_COLUMN_LABELS = {
  en: 'VAT',
  de: 'MwSt',
  es: 'IVA',
  pt: 'IVA',
  it: 'IVA',
  nl: 'BTW',
  pl: 'VAT',
  ru: 'NDS',
  uk: 'PDV',
  fr: 'TVA'
};

/**
 * Get invoice number label for language
 * @param {string} language - Language code
 * @returns {string} Invoice number label
 */
export function getInvoiceNumberLabel(language) {
  return INVOICE_NUMBER_LABELS[language] || INVOICE_NUMBER_LABELS.en;
}

/**
 * Get VAT number label for language
 * @param {string} language - Language code
 * @returns {string} VAT number label
 */
export function getVatNumberLabel(language) {
  return VAT_NUMBER_LABELS[language] || VAT_NUMBER_LABELS.en;
}

/**
 * Get tax column label for language
 * @param {string} language - Language code
 * @returns {string} Tax column label
 */
export function getTaxColumnLabel(language) {
  return TAX_COLUMN_LABELS[language] || TAX_COLUMN_LABELS.en;
}

// ============================================================================
// CURRENCY CONFIGURATION
// ============================================================================

/**
 * Supported currencies
 * @type {Array<string>}
 */
export const SUPPORTED_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD',
  'CNY', 'INR', 'BRL', 'MXN', 'KRW', 'SGD', 'HKD', 'NOK',
  'SEK', 'DKK', 'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'HRK',
  'RUB', 'UAH', 'TRY', 'ZAR', 'AED', 'SAR', 'ILS', 'THB',
  'MYR', 'IDR', 'PHP', 'VND', 'TWD', 'PKR', 'BDT', 'EGP',
  'NGN', 'KES', 'GHS', 'TZS', 'UGX', 'MAD', 'DZD', 'TND',
  'ARS', 'CLP', 'COP', 'PEN', 'VES', 'BOB', 'PYG', 'UYU'
];

/**
 * Currency symbols
 * @type {Object}
 */
export const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CHF: 'CHF',
  CAD: 'C$',
  AUD: 'A$',
  NZD: 'NZ$',
  CNY: '¥',
  INR: '₹',
  BRL: 'R$',
  MXN: '$',
  KRW: '₩',
  SGD: 'S$',
  HKD: 'HK$',
  NOK: 'kr',
  SEK: 'kr',
  DKK: 'kr',
  PLN: 'zl',
  CZK: 'Kc',
  HUF: 'Ft',
  RON: 'lei',
  BGN: 'лв',
  HRK: 'kn',
  RUB: '₽',
  UAH: '₴',
  TRY: '₺',
  ZAR: 'R',
  AED: 'د.إ',
  SAR: '﷼',
  ILS: '₪',
  THB: '฿',
  MYR: 'RM',
  IDR: 'Rp',
  PHP: '₱',
  VND: '₫',
  TWD: 'NT$',
  PKR: '₨',
  BDT: '৳',
  EGP: 'E£',
  NGN: '₦',
  KES: 'KSh',
  GHS: 'GH₵',
  TZS: 'TSh',
  UGX: 'USh',
  MAD: 'د.م.',
  DZD: 'د.ج',
  TND: 'د.ت',
  ARS: '$',
  CLP: '$',
  COP: '$',
  PEN: 'S/',
  VES: 'Bs.',
  BOB: 'Bs.',
  PYG: '₲',
  UYU: '$U'
};

/**
 * Currency full names
 * @type {Object}
 */
export const CURRENCY_TO_LABEL = {
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  JPY: 'Japanese Yen',
  CHF: 'Swiss Franc',
  CAD: 'Canadian Dollar',
  AUD: 'Australian Dollar',
  NZD: 'New Zealand Dollar',
  CNY: 'Chinese Yuan',
  INR: 'Indian Rupee',
  BRL: 'Brazilian Real',
  MXN: 'Mexican Peso',
  KRW: 'South Korean Won',
  SGD: 'Singapore Dollar',
  HKD: 'Hong Kong Dollar',
  NOK: 'Norwegian Krone',
  SEK: 'Swedish Krona',
  DKK: 'Danish Krone',
  PLN: 'Polish Zloty',
  CZK: 'Czech Koruna',
  HUF: 'Hungarian Forint',
  RON: 'Romanian Leu',
  BGN: 'Bulgarian Lev',
  HRK: 'Croatian Kuna',
  RUB: 'Russian Ruble',
  UAH: 'Ukrainian Hryvnia',
  TRY: 'Turkish Lira',
  ZAR: 'South African Rand',
  AED: 'UAE Dirham',
  SAR: 'Saudi Riyal',
  ILS: 'Israeli Shekel',
  THB: 'Thai Baht',
  MYR: 'Malaysian Ringgit',
  IDR: 'Indonesian Rupiah',
  PHP: 'Philippine Peso',
  VND: 'Vietnamese Dong',
  TWD: 'Taiwan Dollar',
  PKR: 'Pakistani Rupee',
  BDT: 'Bangladeshi Taka',
  EGP: 'Egyptian Pound',
  NGN: 'Nigerian Naira',
  KES: 'Kenyan Shilling',
  GHS: 'Ghanaian Cedi',
  TZS: 'Tanzanian Shilling',
  UGX: 'Ugandan Shilling',
  MAD: 'Moroccan Dirham',
  DZD: 'Algerian Dinar',
  TND: 'Tunisian Dinar',
  ARS: 'Argentine Peso',
  CLP: 'Chilean Peso',
  COP: 'Colombian Peso',
  PEN: 'Peruvian Sol',
  VES: 'Venezuelan Bolivar',
  BOB: 'Bolivian Boliviano',
  PYG: 'Paraguayan Guarani',
  UYU: 'Uruguayan Peso'
};

/**
 * Get currency symbol
 * @param {string} currency - Currency code
 * @returns {string} Currency symbol
 */
export function getCurrencySymbol(currency) {
  return CURRENCY_SYMBOLS[currency] || currency;
}

/**
 * Get currency label
 * @param {string} currency - Currency code
 * @returns {string} Currency label
 */
export function getCurrencyLabel(currency) {
  return CURRENCY_TO_LABEL[currency] || currency;
}

/**
 * Format currency option for display
 * @param {string} currency - Currency code
 * @returns {string} Formatted option text
 */
export function formatCurrencyOption(currency) {
  const symbol = getCurrencySymbol(currency);
  const label = getCurrencyLabel(currency);
  return `${currency} ${symbol} ${label}`;
}

// ============================================================================
// DATE FORMAT CONFIGURATION
// ============================================================================

/**
 * Supported date formats
 * @type {Array<string>}
 */
export const SUPPORTED_DATE_FORMATS = [
  'YYYY-MM-DD',
  'DD/MM/YYYY',
  'MM/DD/YYYY',
  'DD.MM.YYYY',
  'DD-MM-YYYY',
  'MMMM D, YYYY',
  'D MMMM YYYY',
  'MMM D, YYYY',
  'D MMM YYYY'
];

/**
 * Get date format preview
 * @param {string} format - Date format
 * @param {Date} date - Date to format (defaults to today)
 * @returns {string} Formatted date preview
 */
export function getDateFormatPreview(format, date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthNamesShort = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const pad = (n) => n.toString().padStart(2, '0');

  switch (format) {
    case 'YYYY-MM-DD':
      return `${year}-${pad(month + 1)}-${pad(day)}`;
    case 'DD/MM/YYYY':
      return `${pad(day)}/${pad(month + 1)}/${year}`;
    case 'MM/DD/YYYY':
      return `${pad(month + 1)}/${pad(day)}/${year}`;
    case 'DD.MM.YYYY':
      return `${pad(day)}.${pad(month + 1)}.${year}`;
    case 'DD-MM-YYYY':
      return `${pad(day)}-${pad(month + 1)}-${year}`;
    case 'MMMM D, YYYY':
      return `${monthNames[month]} ${day}, ${year}`;
    case 'D MMMM YYYY':
      return `${day} ${monthNames[month]} ${year}`;
    case 'MMM D, YYYY':
      return `${monthNamesShort[month]} ${day}, ${year}`;
    case 'D MMM YYYY':
      return `${day} ${monthNamesShort[month]} ${year}`;
    default:
      return `${year}-${pad(month + 1)}-${pad(day)}`;
  }
}

/**
 * Check if date format is default
 * @param {string} format - Date format
 * @returns {boolean} True if default format
 */
export function isDefaultDateFormat(format) {
  return format === SUPPORTED_DATE_FORMATS[0];
}

/**
 * Format date format option for display
 * @param {string} format - Date format
 * @param {Date} date - Date for preview
 * @returns {string} Formatted option text
 */
export function formatDateFormatOption(format, date = new Date()) {
  const preview = getDateFormatPreview(format, date);
  const isDefault = isDefaultDateFormat(format);
  return `${format} (${preview})${isDefault ? ' (default)' : ''}`;
}

// ============================================================================
// INVOICE NUMBER UTILITIES
// ============================================================================

/**
 * Get current month and year string
 * @returns {string} Month/Year string (MM-YYYY)
 */
export function getCurrentMonthAndYear() {
  const now = new Date();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const year = now.getFullYear();
  return `${month}-${year}`;
}

/**
 * Extract month and year from invoice number
 * @param {string} invoiceNumber - Invoice number value
 * @returns {Object|null} Extracted month and year or null
 */
export function extractInvoiceMonthAndYear(invoiceNumber) {
  if (!invoiceNumber) return null;

  const match = invoiceNumber.match(/(\d{1,2})[-/](\d{4})/);
  if (!match) return null;

  return {
    month: match[1].padStart(2, '0'),
    year: match[2]
  };
}

/**
 * Check if invoice number matches current month
 * @param {string} invoiceNumber - Invoice number value
 * @returns {boolean} True if matches current month
 */
export function isInvoiceNumberInCurrentMonth(invoiceNumber) {
  const extracted = extractInvoiceMonthAndYear(invoiceNumber);
  if (!extracted) return false;

  const current = getCurrentMonthAndYear();
  const invoiceMonthYear = `${extracted.month}-${extracted.year}`;

  return invoiceMonthYear === current;
}

/**
 * Generate default invoice number for current month
 * @param {number} number - Invoice sequence number
 * @returns {string} Invoice number value
 */
export function generateInvoiceNumber(number = 1) {
  return `${number}/${getCurrentMonthAndYear()}`;
}

/**
 * Validate invoice number format
 * @param {string} invoiceNumber - Invoice number to validate
 * @returns {Object} Validation result
 */
export function validateInvoiceNumber(invoiceNumber) {
  if (!invoiceNumber || invoiceNumber.trim() === '') {
    return { isValid: false, error: 'Invoice number is required' };
  }

  if (invoiceNumber.length > 100) {
    return { isValid: false, error: 'Invoice number is too long' };
  }

  return { isValid: true, error: null };
}

// ============================================================================
// DATE VALIDATION UTILITIES
// ============================================================================

/**
 * Get today's date in YYYY-MM-DD format
 * @returns {string} Today's date
 */
export function getTodayDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get last day of current month in YYYY-MM-DD format
 * @returns {string} Last day of current month
 */
export function getLastDayOfCurrentMonth() {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const year = lastDay.getFullYear();
  const month = (lastDay.getMonth() + 1).toString().padStart(2, '0');
  const day = lastDay.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Check if date is today
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {boolean} True if date is today
 */
export function isDateToday(dateString) {
  return dateString === getTodayDate();
}

/**
 * Check if date is last day of current month
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {boolean} True if date is last day of current month
 */
export function isDateLastDayOfCurrentMonth(dateString) {
  return dateString === getLastDayOfCurrentMonth();
}

/**
 * Calculate payment due date
 * @param {string} dateOfIssue - Date of issue in YYYY-MM-DD format
 * @param {number} daysUntilDue - Number of days until due
 * @returns {string} Payment due date in YYYY-MM-DD format
 */
export function calculatePaymentDueDate(dateOfIssue, daysUntilDue = 14) {
  const issueDate = new Date(dateOfIssue);
  const dueDate = new Date(issueDate);
  dueDate.setDate(dueDate.getDate() + daysUntilDue);

  const year = dueDate.getFullYear();
  const month = (dueDate.getMonth() + 1).toString().padStart(2, '0');
  const day = dueDate.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Validate date of issue
 * @param {string} dateOfIssue - Date of issue
 * @returns {Object} Validation result
 */
export function validateDateOfIssue(dateOfIssue) {
  if (!dateOfIssue || dateOfIssue.trim() === '') {
    return { isValid: false, error: 'Date of issue is required' };
  }

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateOfIssue)) {
    return { isValid: false, error: 'Invalid date format' };
  }

  return { isValid: true, error: null };
}

/**
 * Validate date of service
 * @param {string} dateOfService - Date of service
 * @returns {Object} Validation result
 */
export function validateDateOfService(dateOfService) {
  if (!dateOfService || dateOfService.trim() === '') {
    return { isValid: false, error: 'Date of service is required' };
  }

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateOfService)) {
    return { isValid: false, error: 'Invalid date format' };
  }

  return { isValid: true, error: null };
}

// ============================================================================
// TEMPLATE CHANGE HANDLERS
// ============================================================================

/**
 * Get form updates for template change
 * @param {string} newTemplate - New template name
 * @param {Object} currentValues - Current form values
 * @returns {Object} Form updates to apply
 */
export function getTemplateChangeUpdates(newTemplate, currentValues) {
  const updates = {};

  if (isStripeTemplate(newTemplate)) {
    updates.dateFormat = STRIPE_DEFAULT_DATE_FORMAT;
    updates['items.0.vatFieldIsVisible'] = true;
  } else {
    updates.dateFormat = SUPPORTED_DATE_FORMATS[0];

    if (currentValues.stripePayOnlineUrl) {
      updates.stripePayOnlineUrl = '';
    }

    if (currentValues.logo) {
      updates.logo = '';
    }
  }

  return updates;
}

/**
 * Get form updates for language change
 * @param {string} newLanguage - New language code
 * @param {string} currentInvoiceNumberValue - Current invoice number value
 * @returns {Object} Form updates to apply
 */
export function getLanguageChangeUpdates(newLanguage, currentInvoiceNumberValue) {
  const invoiceNumberLabel = getInvoiceNumberLabel(newLanguage);
  const vatLabel = getVatNumberLabel(newLanguage);
  const taxLabel = getTaxColumnLabel(newLanguage);

  return {
    'invoiceNumberObject.label': `${invoiceNumberLabel}:`,
    'invoiceNumberObject.value': currentInvoiceNumberValue,
    'seller.vatNoLabelText': vatLabel,
    'buyer.vatNoLabelText': vatLabel,
    taxLabelText: taxLabel
  };
}

// ============================================================================
// HELPER MESSAGE UTILITIES
// ============================================================================

/**
 * General information helper messages
 * @type {Object}
 */
export const GENERAL_HELPER_MESSAGES = {
  en: {
    template: 'Select the design template for your invoice',
    language: 'Select the language of the invoice',
    currency: 'Select the currency of the invoice',
    dateFormat: 'Select the date format of the invoice',
    paymentLink: 'Enter your payment URL. This adds a "Pay Online" button to the PDF invoice.',
    invoiceNumberNotCurrentMonth: 'Invoice number does not match current month',
    dateOfIssueNotToday: 'Date of issue is not today',
    dateOfServiceNotEndOfMonth: 'Date of service is not the last day of the current month'
  },
  de: {
    template: 'Waehlen Sie die Designvorlage fuer Ihre Rechnung',
    language: 'Waehlen Sie die Sprache der Rechnung',
    currency: 'Waehlen Sie die Waehrung der Rechnung',
    dateFormat: 'Waehlen Sie das Datumsformat der Rechnung',
    paymentLink: 'Geben Sie Ihre Zahlungs-URL ein. Dies fuegt eine "Online bezahlen"-Schaltflaeche zur PDF-Rechnung hinzu.',
    invoiceNumberNotCurrentMonth: 'Rechnungsnummer stimmt nicht mit dem aktuellen Monat ueberein',
    dateOfIssueNotToday: 'Rechnungsdatum ist nicht heute',
    dateOfServiceNotEndOfMonth: 'Leistungsdatum ist nicht der letzte Tag des aktuellen Monats'
  },
  es: {
    template: 'Seleccione la plantilla de diseno para su factura',
    language: 'Seleccione el idioma de la factura',
    currency: 'Seleccione la moneda de la factura',
    dateFormat: 'Seleccione el formato de fecha de la factura',
    paymentLink: 'Ingrese su URL de pago. Esto agrega un boton "Pagar en linea" a la factura PDF.',
    invoiceNumberNotCurrentMonth: 'El numero de factura no coincide con el mes actual',
    dateOfIssueNotToday: 'La fecha de emision no es hoy',
    dateOfServiceNotEndOfMonth: 'La fecha de servicio no es el ultimo dia del mes actual'
  },
  pt: {
    template: 'Selecione o modelo de design para sua fatura',
    language: 'Selecione o idioma da fatura',
    currency: 'Selecione a moeda da fatura',
    dateFormat: 'Selecione o formato de data da fatura',
    paymentLink: 'Insira sua URL de pagamento. Isso adiciona um botao "Pagar Online" a fatura PDF.',
    invoiceNumberNotCurrentMonth: 'O numero da fatura nao corresponde ao mes atual',
    dateOfIssueNotToday: 'A data de emissao nao e hoje',
    dateOfServiceNotEndOfMonth: 'A data do servico nao e o ultimo dia do mes atual'
  },
  it: {
    template: 'Seleziona il modello di design per la tua fattura',
    language: 'Seleziona la lingua della fattura',
    currency: 'Seleziona la valuta della fattura',
    dateFormat: 'Seleziona il formato della data della fattura',
    paymentLink: 'Inserisci il tuo URL di pagamento. Questo aggiunge un pulsante "Paga Online" alla fattura PDF.',
    invoiceNumberNotCurrentMonth: 'Il numero della fattura non corrisponde al mese corrente',
    dateOfIssueNotToday: 'La data di emissione non e oggi',
    dateOfServiceNotEndOfMonth: 'La data del servizio non e l\'ultimo giorno del mese corrente'
  },
  nl: {
    template: 'Selecteer de ontwerpsjabloon voor uw factuur',
    language: 'Selecteer de taal van de factuur',
    currency: 'Selecteer de valuta van de factuur',
    dateFormat: 'Selecteer het datumformaat van de factuur',
    paymentLink: 'Voer uw betalings-URL in. Dit voegt een "Online betalen"-knop toe aan de PDF-factuur.',
    invoiceNumberNotCurrentMonth: 'Factuurnummer komt niet overeen met de huidige maand',
    dateOfIssueNotToday: 'Factuurdatum is niet vandaag',
    dateOfServiceNotEndOfMonth: 'Servicedatum is niet de laatste dag van de huidige maand'
  },
  pl: {
    template: 'Wybierz szablon projektu dla swojej faktury',
    language: 'Wybierz jezyk faktury',
    currency: 'Wybierz walute faktury',
    dateFormat: 'Wybierz format daty faktury',
    paymentLink: 'Wprowadz adres URL platnosci. Dodaje to przycisk "Zaplac online" do faktury PDF.',
    invoiceNumberNotCurrentMonth: 'Numer faktury nie odpowiada biezacemu miesiacowi',
    dateOfIssueNotToday: 'Data wystawienia nie jest dzisiejsza',
    dateOfServiceNotEndOfMonth: 'Data uslugi nie jest ostatnim dniem biezacego miesiaca'
  },
  ru: {
    template: 'Vyberite shablon dizayna dlya vashego scheta',
    language: 'Vyberite yazyk scheta',
    currency: 'Vyberite valyutu scheta',
    dateFormat: 'Vyberite format daty scheta',
    paymentLink: 'Vvedite URL platezha. Eto dobavlyaet knopku "Oplatit onlayn" v PDF-schet.',
    invoiceNumberNotCurrentMonth: 'Nomer scheta ne sootvetstvuet tekushchemu mesyatsu',
    dateOfIssueNotToday: 'Data vystavleniya ne segodnya',
    dateOfServiceNotEndOfMonth: 'Data uslugi ne posledniy den tekushchego mesyatsa'
  },
  uk: {
    template: 'Vyberitʹ shablon dyzaynu dlya vashoho rakhunku',
    language: 'Vyberitʹ movu rakhunku',
    currency: 'Vyberitʹ valyutu rakhunku',
    dateFormat: 'Vyberitʹ format daty rakhunku',
    paymentLink: 'Vveditʹ URL platezhu. Tse dodaye knopku "Splatyty onlayn" do PDF-rakhunku.',
    invoiceNumberNotCurrentMonth: 'Nomer rakhunku ne vidpovidaye potochnomu misyatsyu',
    dateOfIssueNotToday: 'Data vystavlennya ne sʹohodni',
    dateOfServiceNotEndOfMonth: 'Data posluhy ne ostanniy denʹ potochnoho misyatsya'
  },
  fr: {
    template: 'Select the design template for your invoice',
    language: 'Select the invoice language',
    currency: 'Select the invoice currency',
    dateFormat: 'Select the invoice date format',
    paymentLink: 'Enter your payment URL. This adds a "Pay Online" button to the PDF invoice.',
    invoiceNumberNotCurrentMonth: 'The invoice number does not match the current month',
    dateOfIssueNotToday: 'The date of issue is not today',
    dateOfServiceNotEndOfMonth: 'The date of service is not the last day of the current month'
  }
};

/**
 * Get general helper messages for a language
 * @param {string} language - Language code
 * @returns {Object} Helper messages
 */
export function getGeneralHelperMessages(language = 'en') {
  return GENERAL_HELPER_MESSAGES[language] || GENERAL_HELPER_MESSAGES.en;
}

// ============================================================================
// FORM FIELD LABELS
// ============================================================================

/**
 * General information field labels
 * @type {Object}
 */
export const GENERAL_FIELD_LABELS = {
  en: {
    template: 'Template',
    logo: 'Company Logo (Optional)',
    paymentLink: 'Payment Link URL (Optional)',
    language: 'Invoice PDF Language',
    currency: 'Currency',
    dateFormat: 'Date Format',
    invoiceNumber: 'Invoice Number',
    invoiceNumberLabel: 'Label',
    invoiceNumberValue: 'Value',
    dateOfIssue: 'Date of Issue',
    dateOfService: 'Date of Service',
    paymentDue: 'Payment Due'
  },
  de: {
    template: 'Vorlage',
    logo: 'Firmenlogo (Optional)',
    paymentLink: 'Zahlungslink-URL (Optional)',
    language: 'Rechnungs-PDF-Sprache',
    currency: 'Waehrung',
    dateFormat: 'Datumsformat',
    invoiceNumber: 'Rechnungsnummer',
    invoiceNumberLabel: 'Bezeichnung',
    invoiceNumberValue: 'Wert',
    dateOfIssue: 'Rechnungsdatum',
    dateOfService: 'Leistungsdatum',
    paymentDue: 'Faelligkeitsdatum'
  },
  es: {
    template: 'Plantilla',
    logo: 'Logo de la empresa (Opcional)',
    paymentLink: 'URL del enlace de pago (Opcional)',
    language: 'Idioma del PDF de la factura',
    currency: 'Moneda',
    dateFormat: 'Formato de fecha',
    invoiceNumber: 'Numero de factura',
    invoiceNumberLabel: 'Etiqueta',
    invoiceNumberValue: 'Valor',
    dateOfIssue: 'Fecha de emision',
    dateOfService: 'Fecha de servicio',
    paymentDue: 'Fecha de vencimiento'
  },
  pt: {
    template: 'Modelo',
    logo: 'Logo da empresa (Opcional)',
    paymentLink: 'URL do link de pagamento (Opcional)',
    language: 'Idioma do PDF da fatura',
    currency: 'Moeda',
    dateFormat: 'Formato de data',
    invoiceNumber: 'Numero da fatura',
    invoiceNumberLabel: 'Rotulo',
    invoiceNumberValue: 'Valor',
    dateOfIssue: 'Data de emissao',
    dateOfService: 'Data do servico',
    paymentDue: 'Data de vencimento'
  },
  it: {
    template: 'Modello',
    logo: 'Logo aziendale (Opzionale)',
    paymentLink: 'URL del link di pagamento (Opzionale)',
    language: 'Lingua del PDF della fattura',
    currency: 'Valuta',
    dateFormat: 'Formato data',
    invoiceNumber: 'Numero fattura',
    invoiceNumberLabel: 'Etichetta',
    invoiceNumberValue: 'Valore',
    dateOfIssue: 'Data di emissione',
    dateOfService: 'Data del servizio',
    paymentDue: 'Scadenza pagamento'
  },
  nl: {
    template: 'Sjabloon',
    logo: 'Bedrijfslogo (Optioneel)',
    paymentLink: 'Betalingslink-URL (Optioneel)',
    language: 'Factuur-PDF-taal',
    currency: 'Valuta',
    dateFormat: 'Datumformaat',
    invoiceNumber: 'Factuurnummer',
    invoiceNumberLabel: 'Label',
    invoiceNumberValue: 'Waarde',
    dateOfIssue: 'Factuurdatum',
    dateOfService: 'Servicedatum',
    paymentDue: 'Vervaldatum'
  },
  pl: {
    template: 'Szablon',
    logo: 'Logo firmy (Opcjonalne)',
    paymentLink: 'URL linku do platnosci (Opcjonalne)',
    language: 'Jezyk PDF faktury',
    currency: 'Waluta',
    dateFormat: 'Format daty',
    invoiceNumber: 'Numer faktury',
    invoiceNumberLabel: 'Etykieta',
    invoiceNumberValue: 'Wartosc',
    dateOfIssue: 'Data wystawienia',
    dateOfService: 'Data uslugi',
    paymentDue: 'Termin platnosci'
  },
  ru: {
    template: 'Shablon',
    logo: 'Logotip kompanii (Neobyazatelno)',
    paymentLink: 'URL ssylki na oplatu (Neobyazatelno)',
    language: 'Yazyk PDF scheta',
    currency: 'Valyuta',
    dateFormat: 'Format daty',
    invoiceNumber: 'Nomer scheta',
    invoiceNumberLabel: 'Metka',
    invoiceNumberValue: 'Znachenie',
    dateOfIssue: 'Data vystavleniya',
    dateOfService: 'Data uslugi',
    paymentDue: 'Srok oplaty'
  },
  uk: {
    template: 'Shablon',
    logo: 'Logotyp kompaniyi (Neoboviazʹkovo)',
    paymentLink: 'URL posylannya na oplatu (Neoboviazʹkovo)',
    language: 'Mova PDF rakhunku',
    currency: 'Valyuta',
    dateFormat: 'Format daty',
    invoiceNumber: 'Nomer rakhunku',
    invoiceNumberLabel: 'Mitka',
    invoiceNumberValue: 'Znachennya',
    dateOfIssue: 'Data vystavlennya',
    dateOfService: 'Data posluhy',
    paymentDue: 'Termin oplaty'
  },
  fr: {
    template: 'Modele',
    logo: 'Logo de l\'entreprise (Optionnel)',
    paymentLink: 'URL du lien de paiement (Optionnel)',
    language: 'Invoice PDF language',
    currency: 'Devise',
    dateFormat: 'Format de date',
    invoiceNumber: 'Numero de facture',
    invoiceNumberLabel: 'Libelle',
    invoiceNumberValue: 'Valeur',
    dateOfIssue: 'Date d\'emission',
    dateOfService: 'Date de service',
    paymentDue: 'Date d\'echeance'
  }
};

/**
 * Get general field labels for a language
 * @param {string} language - Language code
 * @returns {Object} Field labels
 */
export function getGeneralFieldLabels(language = 'en') {
  return GENERAL_FIELD_LABELS[language] || GENERAL_FIELD_LABELS.en;
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/**
 * Default general information values
 * @type {Object}
 */
export const DEFAULT_GENERAL_VALUES = {
  template: 'default',
  logo: '',
  stripePayOnlineUrl: '',
  language: 'en',
  currency: 'USD',
  dateFormat: 'YYYY-MM-DD',
  invoiceNumberObject: {
    label: 'Invoice Number:',
    value: ''
  },
  dateOfIssue: '',
  dateOfService: '',
  paymentDue: '',
  taxLabelText: 'VAT'
};

/**
 * Create default general information values
 * @returns {Object} Default values with current dates
 */
export function createDefaultGeneralValues() {
  const today = getTodayDate();
  const lastDayOfMonth = getLastDayOfCurrentMonth();
  const paymentDue = calculatePaymentDueDate(today, 14);

  return {
    ...DEFAULT_GENERAL_VALUES,
    invoiceNumberObject: {
      label: 'Invoice Number:',
      value: generateInvoiceNumber(1)
    },
    dateOfIssue: today,
    dateOfService: lastDayOfMonth,
    paymentDue: paymentDue
  };
}

// ============================================================================
// EXPORT DEFAULT CONFIGURATION
// ============================================================================

/**
 * Default export with all utilities
 */
export default {
  // Template configuration
  SUPPORTED_TEMPLATES,
  TEMPLATE_TO_LABEL,
  STRIPE_DEFAULT_DATE_FORMAT,
  getTemplateLabel,
  isStripeTemplate,
  isDefaultTemplate,
  getTemplateConfig,

  // Logo upload
  MAX_LOGO_SIZE,
  VALID_LOGO_TYPES,
  validateLogoSize,
  validateLogoType,
  convertFileToBase64,
  processLogoUpload,
  getLogoUploadHelperText,
  getLogoUploadSuccessMessage,

  // Language configuration
  SUPPORTED_LANGUAGES,
  LANGUAGE_TO_LABEL,
  getLanguageLabel,
  isLanguageSupported,
  INVOICE_NUMBER_LABELS,
  VAT_NUMBER_LABELS,
  TAX_COLUMN_LABELS,
  getInvoiceNumberLabel,
  getVatNumberLabel,
  getTaxColumnLabel,

  // Currency configuration
  SUPPORTED_CURRENCIES,
  CURRENCY_SYMBOLS,
  CURRENCY_TO_LABEL,
  getCurrencySymbol,
  getCurrencyLabel,
  formatCurrencyOption,

  // Date format configuration
  SUPPORTED_DATE_FORMATS,
  getDateFormatPreview,
  isDefaultDateFormat,
  formatDateFormatOption,

  // Invoice number utilities
  getCurrentMonthAndYear,
  extractInvoiceMonthAndYear,
  isInvoiceNumberInCurrentMonth,
  generateInvoiceNumber,
  validateInvoiceNumber,

  // Date validation utilities
  getTodayDate,
  getLastDayOfCurrentMonth,
  isDateToday,
  isDateLastDayOfCurrentMonth,
  calculatePaymentDueDate,
  validateDateOfIssue,
  validateDateOfService,

  // Template change handlers
  getTemplateChangeUpdates,
  getLanguageChangeUpdates,

  // Helper messages
  GENERAL_HELPER_MESSAGES,
  getGeneralHelperMessages,

  // Field labels
  GENERAL_FIELD_LABELS,
  getGeneralFieldLabels,

  // Default values
  DEFAULT_GENERAL_VALUES,
  createDefaultGeneralValues
};
